import { isEmpty } from 'lodash';
import { useDomainPath } from '../../../auth/auth.context';
import {
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@material-ui/core';
import React, { useCallback, useEffect, useState } from 'react';
import { get } from '../../../fetch';
import { humanizeKind } from '../../../activities/utils/translations';

/**
 * Interface pour les props du composant ActivitySelect
 */
interface ActivitySelectProps {
  value?: CRM.PublicActivity;
  onChange: (value: CRM.PublicActivity | undefined) => any;
  disabled?: boolean;
  kind?: CRM.ActivityKind;
  displayAll?: boolean;
}

/**
 * Composant de sélection d'activité
 * 
 * Ce composant :
 * - Charge et affiche la liste des activités publiques du domaine
 * - Permet de filtrer par type d'activité (kind)
 * - Affiche un indicateur de chargement pendant le chargement
 * - Supporte l'option "Toutes les Activités" si displayAll est activé
 * 
 * Logique métier :
 * - Récupération des activités via l'API publique
 * - Filtrage par type d'activité si spécifié
 * - Affichage du nom d'affichage et du type traduit
 * - Gestion des états de chargement et d'erreur
 * 
 * @param value Activité actuellement sélectionnée (optionnelle)
 * @param onChange Fonction de callback pour la sélection
 * @param disabled État désactivé du composant (optionnel)
 * @param kind Type d'activité pour filtrer (optionnel)
 * @param displayAll Afficher l'option "Toutes les Activités" (optionnel)
 * @returns Composant React de sélection d'activité
 */
export function ActivitySelect({
  onChange,
  value,
  disabled,
  kind,
  displayAll
}: ActivitySelectProps) {
  const activityPath = useDomainPath('/activity');
  const [activities, setActivities] = useState<CRM.PublicActivity[]>([]);

  const loadActivities = useCallback(() => {
    get<CRM.PublicActivity[]>(`${activityPath}/?public=true`).then(
      domainActivities => {
        if(kind) {
          domainActivities = domainActivities.filter(a => a.kind === kind);
        }
        setActivities(domainActivities);
      },
    );
  }, [activityPath, kind]);

  useEffect(loadActivities, [loadActivities]);

  if (isEmpty(activities)) {
    return <CircularProgress size="1em" />;
  }

  return (
    <FormControl fullWidth variant="outlined">
      <InputLabel id="Activity-select-label">{value?.id ? "Activité" : ""}</InputLabel>
      <Select
        labelId="Activity-select-label"
        label={value?.id ? "Activité" : ""}
        value={value?.id || ''}
        disabled={disabled}
        displayEmpty
        onChange={e => {
          const newSelected = activities.find(
            activity => activity.id === e.target.value,
          );
          onChange(newSelected);
          /*if (newSelected) {
            onChange(newSelected);
          }*/
        }}
      >
        {displayAll && (
          <MenuItem value="">
            Toutes les Activités
          </MenuItem>
        )}
        {activities.map(activity => (
          <MenuItem value={activity.id} key={activity.id}>
            {activity.displayName} ({humanizeKind(activity.kind)})
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
